import type {
  AreaPilotoApi,
  CamadaFeatureApi,
  CamadaGeoApi,
  DashboardSummaryApi,
  DetectedObjectApi,
  DocumentApi,
  FieldValidationNoteApi,
  InspectionApi,
  InspectionQuadraLinkApi,
  PartyApi,
  ProjectApi,
  QualitySummaryApi,
  QuadraApi,
  RascunhoFeicaoApi,
  RascunhoPublishResultApi,
  SpeciesApi,
  TerritorialIndicatorsSummaryApi,
  TerritorialInspectionDetailApi,
  TerritorialInspectionSummaryApi,
  TreeApi,
  TreeMetrics3DApi,
  ValidationIssueApi,
} from "@/lib/api";
import type {
  TerritorialCoverage,
  TerritorialDetectedObject,
  TerritorialFieldValidationNote,
  TerritorialIndicator,
  TerritorialIndicatorsSummary,
  TerritorialInspectionDetail,
  TerritorialInspectionSummary,
  TerritorialLandUse,
  TerritorialRecommendation,
  TerritorialRoad,
  TerritorialTreeInventorySummary,
} from "@/types/territorial-inspection";
import type { AreaPiloto, InspectionQuadraLink, Quadra } from "@/types/territorial";
import type {
  CamadaFeature,
  CamadaGeo,
  RascunhoFeicao,
  RascunhoPublishResult,
  RascunhoStatus,
} from "@/types/editor-territorial";
import type { Species } from "@/mocks/configuracoes";
import type { Projeto } from "@/mocks/projetos";
import type { IndividuoArboreo } from "@/mocks/cta";
import type { Vistoria } from "@/mocks/vistorias";
import type { DocumentItem } from "@/mocks/documentos";
import type { Party } from "@/mocks/partes";
import type { allInconsistencies, projectQualityRanking } from "@/mocks/quality";

type Inconsistency = (typeof allInconsistencies)[number];
type ProjectQualityRank = (typeof projectQualityRanking)[number];

export interface TreeMetric3DRow {
  codigoCta: string;
  especie: string;
  projeto: string;
  bairro: string;
  alturaCampo: number | null;
  altura3D: number | null;
  diametroCopaCampo: number | null;
  diametroCopa3D: number | null;
  volumeCopa: number | null;
  inclinacao: number | null;
  confianca: string;
  risco: string;
  status: string;
  fonte3D: string;
}

export interface DashboardSummaryUi {
  totalProjects: number;
  totalTrees: number;
  totalSpecies: number;
  totalDocuments: number;
  totalParties: number;
  totalRrr: number;
  totalValidationIssues: number;
  totalPointCloudAssets: number;
  totalEnvironmentalUnits: number;
  totalCarbonProjects: number;
  status: string;
}

export function mapDashboardSummary(api: DashboardSummaryApi): DashboardSummaryUi {
  return {
    totalProjects: api.total_projects,
    totalTrees: api.total_trees,
    totalSpecies: api.total_species,
    totalDocuments: api.total_documents,
    totalParties: api.total_parties,
    totalRrr: api.total_rrr,
    totalValidationIssues: api.total_validation_issues,
    totalPointCloudAssets: api.total_point_cloud_assets,
    totalEnvironmentalUnits: api.total_environmental_units,
    totalCarbonProjects: api.total_carbon_projects,
    status: api.status,
  };
}

const ORIGIN_LABELS: Record<string, Species["origin"]> = {
  NATIVA: "nativa",
  EXOTICA: "exótica",
  EXOTICA_INVASORA: "exótica invasora",
  ORNAMENTAL: "ornamental",
  FRUTIFERA: "frutífera",
  NAO_IDENTIFICADA: "não identificada",
};

const SIZE_LABELS: Record<string, Species["size"]> = {
  PEQUENO: "pequeno",
  MEDIO: "médio",
  GRANDE: "grande",
  PALMEIRA: "palmeira",
  ARBUSTIVO: "arbustivo",
};

const SPECIES_STATUS_LABELS: Record<string, Species["status"]> = {
  ATIVO: "ativo",
  INATIVO: "inativo",
  EM_REVISAO: "em revisão",
};

export function mapSpeciesFromApi(api: SpeciesApi): Species {
  return {
    id: String(api.id),
    popularName: api.popular_name,
    scientificName: api.scientific_name,
    family: api.family_name ?? "",
    origin: ORIGIN_LABELS[api.origin ?? ""] ?? "não identificada",
    size: SIZE_LABELS[api.tree_size ?? ""] ?? "médio",
    functionalGroup: api.functional_group ?? "",
    woodDensity: api.wood_density ?? undefined,
    carbonFactor: api.preliminary_carbon_factor ?? undefined,
    isProtected: api.protected_species,
    isInvasive: api.invasive_species,
    isFruitful: api.functional_group === "FRUTIFERA",
    isOrnamental: api.functional_group === "ORNAMENTAL",
    status: SPECIES_STATUS_LABELS[api.status] ?? "ativo",
    observations: api.notes ?? undefined,
  };
}

export function mapProjectFromApi(api: ProjectApi): Projeto {
  return {
    id: api.project_uuid,
    nome: api.name,
    tipo: api.project_type,
    cliente: api.client_id ? String(api.client_id) : "",
    municipio: api.municipality_id ? String(api.municipality_id) : "",
    uf: "",
    status: api.status.toLowerCase(),
    responsavel: "",
    equipe: [],
    metodologia: "FloraIQ-LADM",
    dataInicio: api.created_at,
    dataPrevisao: "",
    dataConclusao: undefined,
    progresso: api.progress_percent,
    totalArvores: api.total_trees,
    vistoriasRealizadas: api.inspection_count,
    vistoriasPendentes: 0,
    arvoresRevisao: 0,
    riscoCritico: api.high_risk_trees,
    especiesIdentificadas: api.species_count,
    fotosAnexadas: 0,
    co2eEstimado: 0,
    areaInventariada: 0,
    areaAbrangencia: "",
    ultimaAtualizacao: api.updated_at,
    alertas: [],
    prioridade: "normal",
  };
}

export function mapTreeFromApi(api: TreeApi): IndividuoArboreo {
  return {
    id: api.uuid,
    codigoCta: api.cta_code ?? "",
    tipo: api.individual_type ?? "",
    especie: api.species_popular_name ?? "",
    nomeCientifico: api.species_scientific_name ?? "",
    nomePopular: api.species_popular_name ?? "",
    projeto: api.project_name ?? "",
    municipio: api.municipality_name ?? "",
    uf: api.municipality_uf ?? "",
    bairro: api.neighborhood ?? "",
    logradouro: api.street ?? "",
    numero: "",
    coordenada: api.latitude != null && api.longitude != null ? `${api.latitude}, ${api.longitude}` : "",
    dap: 0,
    altura: 0,
    diametroCopa: 0,
    estadoFitossanitario: "",
    classeRisco: api.latest_risk_class ?? "",
    recomendacao: "",
    statusRevisao: api.review_status ?? "",
    tecnico: "",
    ultimaVistoria: api.latest_inspection_date ?? "",
    fotos: 0,
    observacao: "",
    fotoUrl: "",
  };
}

export function mapInspectionFromApi(api: InspectionApi): Vistoria {
  return {
    id: api.uuid,
    codigoCta: api.cta_code ?? "",
    especie: api.species_popular_name ?? "",
    nomePopular: api.species_popular_name ?? "",
    projeto: api.project_name,
    data: api.inspection_date,
    tipo: api.inspection_type,
    tecnico: api.technician_name ?? "",
    estadoFitossanitario: "",
    classeRisco: "",
    recomendacao: "",
    fotos: 0,
    status: api.status,
    dap: 0,
    altura: 0,
    diametroCopa: 0,
    parecer: api.notes ?? "",
    sintomas: [],
    interferencias: [],
    municipio: "",
    uf: "",
    bairro: "",
    logradouro: "",
    coordenada: "",
    cicloId: null,
  };
}

const LINKED_TABLE_TO_BINDING_TYPE: Record<string, DocumentItem["primaryBindingType"]> = {
  tree_individuals: "cta",
  projects: "project",
  environmental_units: "area",
  party: "none",
};

export function mapDocumentFromApi(api: DocumentApi): DocumentItem {
  return {
    id: api.uuid,
    type: api.document_type,
    title: api.title,
    number: api.document_number ?? "",
    projectId: api.project_id != null ? String(api.project_id) : null,
    projectName: api.project_name,
    primaryBinding: api.linked_entity_name ?? "",
    primaryBindingType: LINKED_TABLE_TO_BINDING_TYPE[api.linked_entity_table ?? ""] ?? "none",
    additionalBindings: [],
    municipality: "",
    uf: "",
    responsible: "",
    issuingBody: api.issuing_body ?? "",
    issueDate: api.issue_date ?? "",
    expiryDate: api.expiration_date,
    status: api.status,
    description: api.description ?? "",
    version: "",
    replacedById: null,
    fileType: api.mime_type ?? "",
    hasFile: (api.file_count ?? 0) > 0,
    tags: [],
    gisMetadata: null,
    ctaCode: api.linked_entity_table === "tree_individuals" ? api.linked_entity_name : null,
    areaId: null,
    areaName: api.linked_entity_table === "environmental_units" ? api.linked_entity_name : null,
    inspectionId: null,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}

export function mapPartyFromApi(api: PartyApi): Party {
  return {
    id: api.uuid,
    name: api.name,
    type: api.party_type,
    documentId: api.document_number ?? "",
    email: api.email ?? "",
    phone: api.phone ?? "",
    address: api.address ?? "",
    municipality: api.municipality_name ?? "",
    uf: api.uf ?? "",
    institution: api.legal_name ?? "",
    primaryRole: api.role_types ?? "",
    status: api.status,
    activeLinks: api.project_link_count + api.spatial_unit_link_count,
    hasDocuments: api.document_count > 0,
    registrationDate: api.created_at,
    lastUpdate: api.updated_at,
  };
}

const SEVERITY_LABELS: Record<string, string> = {
  CRITICA: "crítica",
  ALTA: "alta",
  MEDIA: "média",
  BAIXA: "baixa",
  INFORMATIVA: "informativa",
};

export function mapValidationIssueFromApi(api: ValidationIssueApi): Inconsistency {
  return {
    id: `${api.rule_code}-${api.entity_id}`,
    severidade: SEVERITY_LABELS[api.severity] ?? api.severity.toLowerCase(),
    regra: api.rule_code,
    codigoCta: "",
    projeto: api.project_id != null ? String(api.project_id) : "",
    descricao: api.message,
    responsavel: "",
    detectadaEm: api.detected_at,
    status: "pendente",
    acaoRecomendada: "",
  };
}

function qualityStatusFromScore(score: number): ProjectQualityRank["status"] {
  if (score >= 90) return "excelente";
  if (score >= 80) return "bom";
  if (score >= 72) return "regular";
  return "atenção";
}

export function mapQualitySummaryFromApi(api: QualitySummaryApi): ProjectQualityRank {
  return {
    projeto: api.project_name,
    score: api.estimated_quality_score,
    arvores: 0,
    inconsistencias: api.open_issue_count,
    status: qualityStatusFromScore(api.estimated_quality_score),
  };
}

const CONFIDENCE_LABELS: Record<string, string> = {
  ALTA: "alta",
  MEDIA: "media",
  BAIXA: "baixa",
};

const VALIDATION_STATUS_LABELS: Record<string, string> = {
  validado: "validado",
  divergente: "divergente",
  sem_3d: "sem_3d",
  // sem medição de campo é tratado como "sem_3d" na UI — não há uma 4ª categoria no filtro hoje.
  sem_campo: "sem_3d",
};

export function mapTreeMetrics3DFromApi(api: TreeMetrics3DApi): TreeMetric3DRow {
  return {
    codigoCta: api.cta_code ?? "",
    especie: api.species_popular_name ?? "",
    projeto: api.project_id != null ? String(api.project_id) : "",
    bairro: "",
    alturaCampo: api.field_height_m,
    altura3D: api.height_3d_m,
    diametroCopaCampo: api.field_crown_diameter_m,
    diametroCopa3D: api.crown_diameter_3d_m,
    volumeCopa: api.crown_volume_3d_m3,
    inclinacao: api.inclination_3d_deg,
    confianca: api.confidence_level ? CONFIDENCE_LABELS[api.confidence_level] ?? "-" : "-",
    risco: "",
    status: VALIDATION_STATUS_LABELS[api.validation_status] ?? api.validation_status,
    fonte3D: api.point_cloud_name ?? "",
  };
}

function mapTerritorialInspectionSummaryFields(
  api: TerritorialInspectionSummaryApi
): TerritorialInspectionSummary {
  return {
    inspectionId: api.inspection_id,
    uuid: api.uuid,
    inspectionCode: api.inspection_code,
    tenantId: api.tenant_id,
    neighborhood: api.neighborhood ?? "",
    blockCode: api.block_code ?? "",
    previousInspectionCode: api.previous_inspection_code,
    inspectionDate: api.inspection_date,
    sourceType: api.source_type,
    sourceReference: api.source_reference,
    imageUri: api.image_uri,
    imageFilename: api.image_filename,
    responsiblePartyName: api.responsible_party_name,
    processingStatus: api.processing_status,
    dataClassification: api.data_classification,
    intendedUse: api.intended_use,
    fieldValidationRequired: api.field_validation_required,
    fieldValidationStatus: api.field_validation_status,
    status: api.status,
    confidenceScore: api.confidence_score,
    estimatedTreeCount: api.estimated_tree_count,
    canopyCoveragePercent: api.canopy_coverage_percent,
    plantingPotentialCount: api.planting_potential_count,
    treeDeficitCount: api.tree_deficit_count,
    riskTreeCount: api.risk_tree_count,
    fieldValidationNotesCount: api.field_validation_notes_count,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}

export function mapTerritorialInspectionSummaryFromApi(
  api: TerritorialInspectionSummaryApi
): TerritorialInspectionSummary {
  return mapTerritorialInspectionSummaryFields(api);
}

export function mapTerritorialInspectionDetailFromApi(
  api: TerritorialInspectionDetailApi
): TerritorialInspectionDetail {
  const roads: TerritorialRoad[] = api.roads.map((r) => ({
    roadName: r.road_name,
    roadType: r.road_type,
    pavementType: r.pavement_type,
    pavementCondition: r.pavement_condition,
    sidewalkCondition: r.sidewalk_condition,
    drainageCondition: r.drainage_condition,
    widthM: r.width_m,
    plantingPotentialCount: r.planting_potential_count,
    treeConflictCount: r.tree_conflict_count,
    notes: r.notes,
  }));

  const landUse: TerritorialLandUse[] = api.land_use.map((l) => ({
    landUseType: l.land_use_type,
    areaPercent: l.area_percent,
    areaM2: l.area_m2,
    intensityLevel: l.intensity_level,
    notes: l.notes,
  }));

  const coverage: TerritorialCoverage[] = api.coverage.map((c) => ({
    coverageType: c.coverage_type,
    areaPercent: c.area_percent,
    areaM2: c.area_m2,
    permeabilityClass: c.permeability_class,
    heatIslandWeight: c.heat_island_weight,
    confidenceScore: c.confidence_score,
    notes: c.notes,
  }));

  const treeInventorySummary: TerritorialTreeInventorySummary | null = api.tree_inventory_summary
    ? {
        estimatedTreeCount: api.tree_inventory_summary.estimated_tree_count,
        adultTreeCount: api.tree_inventory_summary.adult_tree_count,
        youngTreeCount: api.tree_inventory_summary.young_tree_count,
        palmCount: api.tree_inventory_summary.palm_count,
        shrubCount: api.tree_inventory_summary.shrub_count,
        canopyCoveragePercent: api.tree_inventory_summary.canopy_coverage_percent,
        estimatedCanopyAreaM2: api.tree_inventory_summary.estimated_canopy_area_m2,
        plantingPotentialCount: api.tree_inventory_summary.planting_potential_count,
        treeDeficitCount: api.tree_inventory_summary.tree_deficit_count,
        riskTreeCount: api.tree_inventory_summary.risk_tree_count,
        speciesNotes: api.tree_inventory_summary.species_notes,
        notes: api.tree_inventory_summary.notes,
      }
    : null;

  const indicators: TerritorialIndicator[] = api.indicators.map((i) => ({
    indicatorCode: i.indicator_code,
    indicatorName: i.indicator_name,
    category: i.category,
    valueNumeric: i.value_numeric,
    valueText: i.value_text,
    unit: i.unit,
    riskLevel: i.risk_level,
    confidenceScore: i.confidence_score,
    notes: i.notes,
  }));

  const recommendations: TerritorialRecommendation[] = api.recommendations.map((r) => ({
    recommendationType: r.recommendation_type,
    priority: r.priority,
    description: r.description,
    targetLocation: r.target_location,
    estimatedQuantity: r.estimated_quantity,
    expectedImpact: r.expected_impact,
    status: r.status,
  }));

  return {
    ...mapTerritorialInspectionSummaryFields(api),
    reportTitle: api.report_title,
    reportText: api.report_text,
    roads,
    landUse,
    coverage,
    treeInventorySummary,
    indicators,
    recommendations,
  };
}

export function mapDetectedObjectFromApi(api: DetectedObjectApi): TerritorialDetectedObject {
  return {
    id: api.id,
    uuid: api.uuid,
    objectCode: api.object_code,
    objectType: api.object_type,
    objectLabel: api.object_label,
    confidenceScore: api.confidence_score,
    detectionMethod: api.detection_method,
    heightEstimatedM: api.height_estimated_m,
    crownDiameterEstimatedM: api.crown_diameter_estimated_m,
    canopyAreaM2: api.canopy_area_m2,
    conditionStatus: api.condition_status,
    riskLevel: api.risk_level,
    notes: api.notes,
  };
}

export function mapFieldValidationNoteFromApi(api: FieldValidationNoteApi): TerritorialFieldValidationNote {
  return {
    id: api.id,
    uuid: api.uuid,
    noteType: api.note_type,
    description: api.description,
    priority: api.priority,
    status: api.status,
    responsiblePartyName: api.responsible_party_name,
    createdAt: api.created_at,
  };
}

export function mapTerritorialIndicatorsSummaryFromApi(
  api: TerritorialIndicatorsSummaryApi
): TerritorialIndicatorsSummary {
  return {
    avgTreeCoverageByNeighborhood: api.avg_tree_coverage_by_neighborhood.map((i) => ({
      neighborhood: i.neighborhood,
      totalInspections: i.total_inspections,
      avgCanopyCoveragePercent: i.avg_canopy_coverage_percent,
      avgEstimatedTreeCount: i.avg_estimated_tree_count,
    })),
    heatIslandRanking: api.heat_island_ranking.map((i) => ({
      neighborhood: i.neighborhood,
      blockCode: i.block_code,
      heatIslandRiskScore: i.heat_island_risk_score,
      riskLevel: i.risk_level,
    })),
    estimatedTreesByBlock: api.estimated_trees_by_block.map((i) => ({
      neighborhood: i.neighborhood,
      blockCode: i.block_code,
      estimatedTreeCount: i.estimated_tree_count,
      riskTreeCount: i.risk_tree_count,
      plantingPotentialCount: i.planting_potential_count,
    })),
    plantingPotentialByRoad: api.planting_potential_by_road.map((i) => ({
      roadName: i.road_name,
      roadSegments: i.road_segments,
      totalPlantingPotential: i.total_planting_potential,
    })),
    avgImpermeabilization: api.avg_impermeabilization.map((i) => ({
      neighborhood: i.neighborhood,
      avgImpermeabilizationPercent: i.avg_impermeabilization_percent,
    })),
    treeDeficitRanking: api.tree_deficit_ranking.map((i) => ({
      neighborhood: i.neighborhood,
      blockCode: i.block_code,
      treeDeficitCount: i.tree_deficit_count,
      plantingPotentialCount: i.planting_potential_count,
      canopyCoveragePercent: i.canopy_coverage_percent,
    })),
  };
}

// --- Editor Territorial WebGIS -------------------------------------------

export function mapRascunhoFeicaoFromApi(api: RascunhoFeicaoApi): RascunhoFeicao {
  return {
    id: api.id,
    projetoId: api.projeto_id,
    quadraId: api.quadra_id,
    projetoRefId: api.projeto_ref_id,
    quadraRefId: api.quadra_ref_id,
    areaPilotoRefId: api.area_piloto_ref_id,
    camadaDestino: api.camada_destino,
    tipoObjeto: api.tipo_objeto,
    tipoGeometria: api.tipo_geometria,
    geom: api.geom,
    propriedades: api.propriedades,
    status: api.status as RascunhoStatus,
    fonte: api.fonte,
    validacao: api.validacao,
    areaM2: api.area_m2,
    comprimentoM: api.comprimento_m,
    perimetroM: api.perimetro_m,
    publicadoSchema: api.publicado_schema,
    publicadoTabela: api.publicado_tabela,
    publicadoId: api.publicado_id,
    criadoPor: api.criado_por,
    revisadoPor: api.revisado_por,
    aprovadoPor: api.aprovado_por,
    publicadoPor: api.publicado_por,
    criadoEm: api.criado_em,
    atualizadoEm: api.atualizado_em,
    revisadoEm: api.revisado_em,
    aprovadoEm: api.aprovado_em,
    publicadoEm: api.publicado_em,
  };
}

export function mapCamadaGeoFromApi(api: CamadaGeoApi): CamadaGeo {
  return {
    id: api.id,
    nome: api.nome,
    schemaName: api.schema_name,
    tableName: api.table_name,
    camadaDestino: api.camada_destino,
    tipoObjeto: api.tipo_objeto,
    tipoGeometria: api.tipo_geometria,
    descricao: api.descricao,
    status: api.status,
  };
}

export function mapCamadaFeatureFromApi(api: CamadaFeatureApi): CamadaFeature {
  return {
    id: api.id,
    rascunhoId: api.rascunho_id,
    quadraId: api.quadra_id,
    geom: api.geom,
    propriedades: api.propriedades,
    fonte: api.fonte,
    validacao: api.validacao,
    publicadoEm: api.publicado_em,
  };
}

export function mapRascunhoPublishResultFromApi(api: RascunhoPublishResultApi): RascunhoPublishResult {
  return {
    rascunhoId: api.rascunho_id,
    camadaDestino: api.camada_destino,
    publicadoId: api.publicado_id,
    objetoTerritorialId: api.objeto_territorial_id,
    status: api.status,
  };
}

// --- Território > Área piloto / Quadras (camada operacional real) --------

export function mapAreaPilotoFromApi(api: AreaPilotoApi): AreaPiloto {
  return {
    id: api.id,
    uuid: api.uuid,
    tenantId: api.tenant_id,
    projectId: api.project_id,
    municipalityId: api.municipality_id,
    municipalityName: api.municipality_name,
    uf: api.uf,
    name: api.name,
    code: api.code,
    neighborhood: api.neighborhood,
    expectedBlockCount: api.expected_block_count,
    totalQuadras: api.total_quadras,
    totalTrees: api.total_trees,
    totalPostes: api.total_postes,
    totalRedesAereas: api.total_redes_aereas,
    totalConflitosAltaCritica: api.total_conflitos_alta_critica,
    areaHa: api.area_ha,
    geom: api.geom,
    status: api.status,
    updatedAt: api.updated_at,
  };
}

export function mapQuadraFromApi(api: QuadraApi): Quadra {
  return {
    id: api.id,
    uuid: api.uuid,
    tenantId: api.tenant_id,
    areaPilotoId: api.area_piloto_id,
    areaPilotoName: api.area_piloto_name,
    spatialUnitId: api.spatial_unit_id,
    code: api.code,
    name: api.name,
    neighborhood: api.neighborhood,
    areaM2: api.area_m2,
    perimeterM: api.perimeter_m,
    totalTrees: api.total_trees,
    totalIndividuosArboreos: api.total_individuos_arboreos,
    totalPostes: api.total_postes,
    totalRedesAereas: api.total_redes_aereas,
    totalInterferencias: api.total_interferencias,
    totalInterferenciasCriticas: api.total_interferencias_criticas,
    areaPermeavelM2: api.area_permeavel_m2,
    areaImpermeavelM2: api.area_impermeavel_m2,
    geom: api.geom,
    status: api.status,
    updatedAt: api.updated_at,
  };
}

export function mapInspectionQuadraLinkFromApi(api: InspectionQuadraLinkApi): InspectionQuadraLink {
  return {
    inspectionId: api.inspection_id,
    inspectionUuid: api.inspection_uuid,
    inspectionCode: api.inspection_code,
    tenantId: api.tenant_id,
    projectId: api.project_id,
    inspectionAreaPilotoId: api.inspection_area_piloto_id,
    quadraId: api.quadra_id,
    quadraUuid: api.quadra_uuid,
    quadraAreaPilotoId: api.quadra_area_piloto_id,
    quadraCode: api.quadra_code,
    quadraName: api.quadra_name,
    inspectionBlockCode: api.inspection_block_code,
    inspectionNeighborhood: api.inspection_neighborhood,
    quadraNeighborhood: api.quadra_neighborhood,
    inspectionStatus: api.inspection_status,
    processingStatus: api.processing_status,
    fieldValidationStatus: api.field_validation_status,
    aiModel: api.ai_model,
    analysisVersion: api.analysis_version,
    inspectionDate: api.inspection_date,
    imageFilename: api.image_filename,
    confidenceScore: api.confidence_score,
    linkMethod: api.link_method,
    linkConfidence: api.link_confidence,
    totalCodeMatches: api.total_code_matches,
    treeCanopyPercent: api.tree_canopy_percent,
    imperviousAreaPercent: api.impervious_area_percent,
    heatIslandRisk: api.heat_island_risk,
    treeDeficitLevel: api.tree_deficit_level,
    estimatedTreeCount: api.estimated_tree_count,
    plantingPotentialCount: api.planting_potential_count,
    inventoryCanopyCoveragePercent: api.inventory_canopy_coverage_percent,
    quadraAreaM2: api.quadra_area_m2,
    quadraPerimeterM: api.quadra_perimeter_m,
    geom: api.geom,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}
